import { Injectable, inject } from '@angular/core';
import { Client } from '@enezatelecoms/eneza-models';
import { User } from 'firebase/auth';
import {
  addDoc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { Observable } from 'rxjs';
import { FirebaseService } from '../../data/firebase.service';
import { collectionData, docData } from '../../firebase/firestore';
import { ENEZA_RESELLER_FIRESTORE_PATHS } from './firestore-paths';
import { FloatHistory, SmsCreditsHistory } from './float.history.model';
import { ResellerHistoryService } from './history.service';
import { Reseller } from './reseller.model';

@Injectable({
  providedIn: 'root',
})
export class ResellerClientService {
  private fbService = inject(FirebaseService);
  private historyService = inject(ResellerHistoryService);
  private paths = inject(ENEZA_RESELLER_FIRESTORE_PATHS);

  getByDocId(uid: string): Observable<any> {
    const docRef = this.fbService.getDocRef(this.paths.FB_CLIENTS, uid);
    return docData(docRef, { idField: 'docId' }) as Observable<Client>;
  }

  getResellerModules = async (resellerCode: string) => {
    let resellerModules: any = [];

    const colRef = this.fbService.getColRef(this.paths.FB_RESELLERS);
    const q = query(colRef, where('resellerCode', '==', resellerCode));
    const snapshot = await getDocs(q);

    if (snapshot && snapshot.docs[0] && snapshot.docs[0].exists) {
      const reseller = snapshot.docs[0].data() as Reseller;
      if (reseller.modules) resellerModules = reseller.modules;
    }

    return resellerModules;
  };

  /**
   * CODE re-use is not permitted across the systems!
   */
  getByCode = async (clientCode: string) => {
    const clientRef = this.fbService.getColRef(this.paths.FB_CLIENTS);
    const q = query(clientRef, where('clientCode', '==', clientCode));
    const snapshot1 = await getDocs(q);
    if (snapshot1 && snapshot1.docs[0] && snapshot1.docs[0].exists) {
      const client = snapshot1.docs[0].data() as Client;
      client.docId = snapshot1.docs[0].id;
      return client;
    }

    const resellerRef = this.fbService.getColRef(this.paths.FB_RESELLERS);
    const q2 = query(resellerRef, where('resellerCode', '==', clientCode));
    const snapshot2 = await getDocs(q2);
    if (snapshot2 && snapshot2.docs[0] && snapshot2.docs[0].exists) {
      const reseller = snapshot2.docs[0].data() as Reseller;
      reseller.docId = snapshot2.docs[0].id;
      return reseller;
    }

    return null;
  };

  getResellerByCode = async (resellerCode: string) => {
    const colRef = this.fbService.getColRef(this.paths.FB_RESELLERS);
    const q = query(colRef, where('resellerCode', '==', resellerCode));
    const snapshot = await getDocs(q);

    return snapshot.empty
      ? null
      : ({
          ...snapshot.docs[0].data(),
          docId: snapshot.docs[0].id,
        } as Reseller);
  };

  getByClientEmail = async (email: string) => {
    const colRef = this.fbService.getColRef(this.paths.FB_CLIENTS);
    const q = query(colRef, where('email', '==', email));
    return getDocs(q);
  };

  getByPhoneNumber = async (phoneNumber: string) => {
    const colRef = this.fbService.getColRef(this.paths.FB_CLIENTS);
    const q = query(colRef, where('phoneNumber', '==', phoneNumber));
    return getDocs(q);
  };

  doCreate = async (adminUser: User, data: any): Promise<any> => {
    data.createdBy = data.updatedBy = adminUser?.uid;
    data.createdByEmail = data.updatedByEmail = adminUser?.email;
    data.dateCreated = data.lastUpdated = serverTimestamp();
    data.sendpasswordReset = data.verifyEmail = true;
    const colRef = this.fbService.getColRef(this.paths.FB_CLIENTS_NEW);
    await addDoc(colRef, data);
  };

  doUpdate = async (
    adminUser: User,
    clientId: string,
    data: any,
    resellerCode: string,
  ): Promise<any> => {
    data.updatedBy = adminUser?.uid;
    data.updatedByEmail = adminUser?.email;
    data.lastUpdated = serverTimestamp();

    const docRef = this.fbService.getDocRef(this.paths.FB_CLIENTS, clientId);
    await updateDoc(docRef, data);

    data.resellerCode = resellerCode;
    await this.historyService.addClientHistoryItem(adminUser, clientId, data);
  };

  doUpdateFloat = async (adminUser: User, clientId: string, data: FloatHistory): Promise<any> => {
    const update = {
      doneBy: adminUser?.uid,
      updatedByEmail: adminUser.email,
      changeAmount: data.amount,
      float: data.currentFloat,
      note: data.note,
      lastUpdated: serverTimestamp(),
    };

    const docRef = this.fbService.getDocRef(this.paths.FB_CLIENTS, clientId);
    await updateDoc(docRef, {
      float: increment(data.amount),
      lastUpdated: serverTimestamp(),
    });

    await this.historyService.addCustomClientHistoryItem(
      adminUser,
      clientId,
      update,
      this.paths.HISTORY_MANUAL_FLOAT_EDITS,
    );
    await this.historyService.addClientFloatHistory(adminUser, clientId, data);
  };

  doUpdateSmsCredits = async (
    adminUser: User,
    clientId: string,
    data: SmsCreditsHistory,
  ): Promise<any> => {
    const update = {
      doneBy: adminUser?.uid,
      updatedByEmail: adminUser.email,
      changeAmount: data.amount,
      credits: data.currentCredits,
      rate: data.rate,
      previousCredits: data.previousCredits,
      currentCredits: data.currentCredits,
      resellerCode: data.resellerCode,
      clientCode: data.clientCode,
      note: data.note || 'null',
      lastUpdated: serverTimestamp(),
    };

    const docRef = this.fbService.getDocRef(this.paths.FB_CLIENTS, clientId);
    await updateDoc(docRef, {
      'sms.credits': increment(data.amount),
      clientCode: data.clientCode,
      resellerCode: data.resellerCode,
      lastUpdated: serverTimestamp(),
    });

    await this.historyService.addCustomClientHistoryItem(
      adminUser,
      clientId,
      update,
      this.paths.HISTORY_MANUAL_SMS_CREDITS_EDITS,
    );
    await this.historyService.addClientSmsCreditsHistory(adminUser, clientId, data);
  };

  doDelete = async (
    adminUser: User,
    docId: string,
    note: string,
    resellerCode: string,
  ): Promise<any> => {
    const data: any = {
      note: note,
      userId: docId,
      resellerCode,
      toDelete: true,
      deletedBy: adminUser?.uid,
      deletedByEmail: adminUser.email,
      dateDeleted: serverTimestamp(),
    };

    const docRef = this.fbService.getDocRef(this.paths.FB_CLIENTS, docId);
    await updateDoc(docRef, data);

    await this.historyService.addClientHistoryItem(adminUser, docId, data);
  };

  // Promise based version of getByDocId
  getClientByDocId = async (docId: string) => {
    const docRef = this.fbService.getDocRef(this.paths.FB_CLIENTS, docId);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? ({ ...snapshot.data(), docId: snapshot.id } as Client) : null;
  };

  getResellerClients = (resellerCode: string) => {
    const colRef = this.fbService.getColRef(this.paths.FB_CLIENTS);
    const q = query(colRef, where('resellerCode', '==', resellerCode));
    return collectionData(q, { idField: 'docId' }) as Observable<Client[]>;
  };
}
